import { useState } from 'react';
import { ThemeProvider } from './contexts/ThemeContext';
import { Sidebar } from './components/Sidebar';
import { EngagementTable } from './components/EngagementTable';
import Workspace from './Workspace';
import SubGroupingManager from './components/SubGroupingManager';
import JournalEntryList from './components/JournalEntryList';
import MAdjustmentList from './components/MAdjustmentList';
import SettingsView from './components/SettingsView';
import type { Engagement, JournalEntryHeader } from './types';
import { getJournalEntryHeader } from './db/database';

type View = 'library' | 'workspace' | 'subgroupings' | 'journalEntries' | 'mAdjustments' | 'settings';

function App() {
  const [view, setView] = useState<View>('library');
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);
  const [activeEngagement, setActiveEngagement] = useState<Engagement | null>(null);
  const [jeSearchHint, setJeSearchHint] = useState('');
  const [selectedJournalEntryHeader, setSelectedJournalEntryHeader] = useState<JournalEntryHeader | null>(null);
  const [selectedMAdjHeader, setSelectedMAdjHeader] = useState<JournalEntryHeader | null>(null);

  const handleToggleSidebar = () => {
    setIsSidebarCollapsed(!isSidebarCollapsed);
  };

  const handleOpenEngagement = (engagement: Engagement) => {
    setSelectedJournalEntryHeader(null);
    setActiveEngagement(engagement);
    setView('workspace');
  };

  const handleNavigateToLibrary = () => {
    setSelectedJournalEntryHeader(null);
    setView('library');
  };

  const handleNavigateToWorkspace = () => {
    if (activeEngagement) {
      setSelectedJournalEntryHeader(null);
      setView('workspace');
    }
  };

  const handleNavigateToSubGroupings = () => {
    if (activeEngagement) {
      setSelectedJournalEntryHeader(null);
      setView('subgroupings');
    }
  };

  const handleNavigateToJournalEntries = (hint = '') => {
    if (activeEngagement) {
      setSelectedJournalEntryHeader(null);
      setJeSearchHint(hint);
      setView('journalEntries');
    }
  };

  const handleNavigateToMAdjustments = () => {
    if (activeEngagement) setView('mAdjustments');
  };

  const handleNavigateToSettings = () => {
    if (activeEngagement) setView('settings');
  };

  const handleOpenJournalEntry = async (headerId: string) => {
    if (!activeEngagement) return;
    const header = await getJournalEntryHeader(headerId);
    if (!header) return;
    setSelectedJournalEntryHeader(header);
    setView('journalEntries');
  };

  const handleOpenMAdjustment = async (headerId: string) => {
    if (!activeEngagement) return;
    const header = await getJournalEntryHeader(headerId);
    if (!header) return;
    setSelectedMAdjHeader(header);
    setView('mAdjustments');
  };

  return (
    <ThemeProvider>
      <div className="flex h-screen overflow-hidden bg-taxtrail-bg dark:bg-neutral-900 transition-colors duration-300">
        {/* Sidebar */}
        <Sidebar
          collapsed={isSidebarCollapsed}
          onToggle={handleToggleSidebar}
          currentView={view}
          onNavigateToLibrary={handleNavigateToLibrary}
          onNavigateToWorkspace={handleNavigateToWorkspace}
          onNavigateToSubGroupings={handleNavigateToSubGroupings}
          onNavigateToJournalEntries={handleNavigateToJournalEntries}
          onNavigateToMAdjustments={handleNavigateToMAdjustments}
          onNavigateToSettings={handleNavigateToSettings}
          hasActiveEngagement={activeEngagement !== null}
        />

        {/* Main Content */}
        <main
          className={`flex-1 overflow-hidden transition-all duration-300 ${
            isSidebarCollapsed ? 'ml-16' : 'ml-56'
          }`}
        >
          {view === 'library' ? (
            <EngagementTable onOpenEngagement={handleOpenEngagement} />
          ) : view === 'subgroupings' ? (
            activeEngagement && (
              <SubGroupingManager engagement={activeEngagement} />
            )
          ) : view === 'journalEntries' ? (
            activeEngagement && (
              <JournalEntryList
                engagement={activeEngagement}
                searchHint={jeSearchHint}
                initialHeader={selectedJournalEntryHeader}
                onInitialHeaderConsumed={() => setSelectedJournalEntryHeader(null)}
              />
            )
          ) : view === 'mAdjustments' ? (
            activeEngagement && (
                  <MAdjustmentList
                engagement={activeEngagement}
                initialHeader={selectedMAdjHeader}
                onInitialHeaderConsumed={() => setSelectedMAdjHeader(null)}
              />
            )
          ) : view === 'settings' ? (
            activeEngagement && (
              <SettingsView engagement={activeEngagement} />
            )
          ) : (
            activeEngagement && (
              <Workspace
                engagement={activeEngagement}
                isSidebarCollapsed={isSidebarCollapsed}
                onOpenJournalEntry={handleOpenJournalEntry}
                onOpenMAdjustment={handleOpenMAdjustment}
              />
            )
          )}
        </main>
      </div>
    </ThemeProvider>
  );
}

export default App;
